import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Form, Input, InputNumber, Modal, Segmented, Select, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { TokenService } from "../../services";
import { TokenRecord, TokenOperationDto, UserTokenInfo } from "../../types/token";
import ErrorHandler from "../../utils/errorHandler";
import TokenManager from "../../utils/tokenManager";
import MainLayout from "../../components/layout/MainLayout";
import {
  Avatar,
  CREDIT_OP,
  EmptyState,
  Icon,
  Pager,
  PersonCell,
  StatusBadge,
  Surface,
  ToolbarGroup,
  formatNumber,
  formatTime,
} from "../../components/ui";
import "./TokenRecords.scss";

const { RangePicker } = DatePicker;

type Filters = { keyword: string; operationType?: string; range: [Dayjs, Dayjs] | null };

const isPhone = (value: string) => /^1[3-9]\d{9}$/.test(value);

const opBadge = (type: string) => {
  const hit = CREDIT_OP[type];
  return <StatusBadge tone={hit ? hit[1] : "ink"}>{hit ? hit[0] : type}</StatusBadge>;
};

const amountCell = (amount: number, type: string) => {
  // 消耗记为负数展示；其余为入账
  const negative = type === "consume" || amount < 0;
  const value = Math.abs(Number(amount) || 0);
  return <span className={`mono ${negative ? "ddp-num-neg" : "ddp-num-pos"}`}>{negative ? "−" : "+"}{formatNumber(value)}</span>;
};

const TokenRecordsPage: React.FC = () => {
  const payload = TokenManager.getUserFromToken(TokenManager.getAccessToken() || "") || {};
  const roles: string[] = Array.isArray(payload.roles) ? payload.roles : [];
  const isAdmin = roles.includes("admin");
  const isManager = isAdmin || roles.includes("org_manager");

  const [records, setRecords] = useState<TokenRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState({ current: 1, size: 20 });
  const [keywordInput, setKeywordInput] = useState("");
  const [filters, setFilters] = useState<Filters>({ keyword: "", range: null });
  const [account, setAccount] = useState<UserTokenInfo | null>(null);
  const [myBalance, setMyBalance] = useState<number | null>(null);
  const [opOpen, setOpOpen] = useState(false);
  const [opSaving, setOpSaving] = useState(false);
  const [opForm] = Form.useForm();

  const searchArgs = useMemo(() => {
    const keyword = filters.keyword;
    const accountId = keyword && !isPhone(keyword) && /^\d+$/.test(keyword) ? Number(keyword) : undefined;
    const phone = keyword && isPhone(keyword) ? keyword : undefined;
    return {
      accountId,
      phone,
      operationType: filters.operationType,
      startDate: filters.range?.[0]?.format("YYYY-MM-DD"),
      endDate: filters.range?.[1]?.format("YYYY-MM-DD"),
    };
  }, [filters]);

  // 管理视角：按账号 / 手机号查流水
  const loadManagerRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { accountId, phone, operationType, startDate, endDate } = searchArgs;
      const response = await TokenService.getUserTokenLogs(accountId, phone, operationType, startDate, endDate, page.current, page.size);
      if (response.code === 0 && response.data) {
        const list = response.data.list || [];
        setRecords(list);
        setTotal(response.data.total || 0);
        const focusId = accountId || (phone && list[0]?.accountId);
        if (focusId) {
          TokenService.getUserToken(focusId)
            .then((res) => setAccount(res.code === 0 && res.data ? res.data : null))
            .catch(() => setAccount(null));
        } else {
          setAccount(null);
        }
      } else {
        message.error(response.msg || "获取算力记录失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [searchArgs, page]);

  // 客户视角：自己的余额与流水
  const loadMine = useCallback(async () => {
    setLoading(true);
    try {
      const [balanceRes, logsRes] = await Promise.all([TokenService.getMyBalance(), TokenService.getMyLogs()]);
      if (balanceRes.code === 0 && balanceRes.data) setMyBalance(balanceRes.data.balance);
      if (logsRes.code === 0 && logsRes.data) {
        const list = Array.isArray(logsRes.data) ? logsRes.data : [];
        setRecords(list);
        setTotal(list.length);
      } else {
        message.error(logsRes.msg || "获取算力记录失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isManager) loadManagerRecords();
  }, [isManager, loadManagerRecords]);

  useEffect(() => {
    if (!isManager) loadMine();
  }, [isManager, loadMine]);

  const applyKeyword = (value: string) => {
    const keyword = value.trim();
    if (keyword && !/^\d+$/.test(keyword)) {
      message.warning("请输入账号 ID 或 11 位手机号");
      return;
    }
    setFilters((prev) => ({ ...prev, keyword }));
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const focusAccount = (record: TokenRecord) => {
    const value = String(record.accountId);
    setKeywordInput(value);
    setFilters((prev) => ({ ...prev, keyword: value }));
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const resetFilters = () => {
    setKeywordInput("");
    setFilters({ keyword: "", range: null });
    setPage((prev) => ({ ...prev, current: 1 }));
  };

  const openOperation = () => {
    opForm.resetFields();
    opForm.setFieldsValue({ operationType: "recharge" });
    setOpOpen(true);
  };

  const submitOperation = async (values: TokenOperationDto) => {
    if (!account) return;
    setOpSaving(true);
    try {
      const response = await TokenService.performOperationForUser(account.accountId, { ...values, accountId: account.accountId });
      if (response.code === 0) {
        message.success("算力调整成功");
        setOpOpen(false);
        loadManagerRecords();
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setOpSaving(false);
    }
  };

  const allAccounts = isManager && !account;
  const visibleRecords = isManager ? records : records.slice((page.current - 1) * page.size, page.current * page.size);
  const filtered = !!(filters.keyword || filters.operationType || filters.range);

  const columns: ColumnsType<TokenRecord> = [
    { title: "操作时间", dataIndex: "createdAt", key: "createdAt", width: 170, render: (time: string) => <span className="mono text-muted">{formatTime(time)}</span> },
    ...(allAccounts
      ? [
          {
            title: "账号",
            key: "account",
            width: 210,
            render: (_: unknown, record: TokenRecord) => (
              <PersonCell name={record.account?.username || `账号 ${record.accountId}`} sub={record.account?.phone || `ID ${record.accountId}`} onClick={() => focusAccount(record)} />
            ),
          },
        ]
      : []),
    { title: "类型", dataIndex: "operationType", key: "operationType", width: 120, render: opBadge },
    {
      title: "变动",
      dataIndex: "operationAmount",
      key: "operationAmount",
      width: 110,
      align: "right" as const,
      render: (amount: number, record: TokenRecord) => amountCell(amount, record.operationType),
    },
    { title: "变动前", dataIndex: "balanceBefore", key: "balanceBefore", width: 100, align: "right" as const, render: (v?: number) => <span className="mono text-muted">{formatNumber(v)}</span> },
    { title: "变动后", dataIndex: "balanceAfter", key: "balanceAfter", width: 100, align: "right" as const, render: (v?: number) => <span className="mono">{formatNumber(v)}</span> },
    { title: "关联任务", dataIndex: "taskId", key: "taskId", width: 110, render: (taskId?: number) => (taskId ? <span className="mono">#{taskId}</span> : <span className="text-faint">—</span>) },
    { title: "备注", dataIndex: "remark", key: "remark", ellipsis: true, render: (remark?: string) => remark || <span className="text-faint">—</span> },
  ];

  const summary = isManager ? (
    account ? (
      <section className="credits-summary" aria-label="账号算力概览">
        <div className="credits-summary__who">
          <Avatar text={account.account?.username || account.account?.phone} size={40} />
          <div>
            <strong>{account.account?.username || `账号 ${account.accountId}`}</strong>
            <span className="mono">{[account.account?.phone, `ID ${account.accountId}`].filter(Boolean).join(" · ")}</span>
          </div>
        </div>
        <span className="credits-summary__rule" aria-hidden="true" />
        <div className="credits-summary__stat">
          <span>当前余额</span>
          <strong>
            {formatNumber(account.balance)} <small>算力</small>
          </strong>
        </div>
        <div className="credits-summary__actions">
          <Button onClick={resetFilters}>查看全部账号</Button>
          {isAdmin && (
            <Button type="primary" icon={<Icon name="sliders" />} onClick={openOperation}>
              调整算力
            </Button>
          )}
        </div>
      </section>
    ) : (
      <section className="credits-summary credits-summary--hint">
        <Icon name="info" size={18} />
        <span>输入账号 ID 或手机号查看某个账号的余额{isAdmin ? "，并在这里调整算力" : ""}；也可以点击表格中的账号。</span>
      </section>
    )
  ) : (
    <section className="credits-summary" aria-label="我的算力">
      <div className="credits-summary__stat">
        <span>我的算力余额</span>
        <strong>
          {formatNumber(myBalance)} <small>算力</small>
        </strong>
      </div>
      <span className="credits-summary__rule" aria-hidden="true" />
      <p className="credits-summary__note">成功完成的任务才会扣除算力；24 小时内同一商品只扣一次。需要充值请联系为您开通账号的团队管理员。</p>
    </section>
  );

  return (
    <MainLayout description={isManager ? undefined : "你的算力余额，以及每一笔充值、赠送与消耗"}>
      {summary}
      <Surface
        toolbar={
          isManager ? (
            <>
              <ToolbarGroup>
                <Input
                  className="credits-page__search"
                  allowClear
                  prefix={<Icon name="search" />}
                  placeholder="账号 ID 或手机号"
                  aria-label="账号 ID 或手机号"
                  value={keywordInput}
                  onChange={(e) => {
                    setKeywordInput(e.target.value);
                    if (!e.target.value && filters.keyword) applyKeyword("");
                  }}
                  onPressEnter={() => applyKeyword(keywordInput)}
                />
                <Select
                  className="credits-page__type"
                  allowClear
                  placeholder="全部类型"
                  aria-label="操作类型"
                  value={filters.operationType}
                  options={Object.entries(CREDIT_OP).map(([value, [label]]) => ({ value, label }))}
                  onChange={(value) => {
                    setFilters((prev) => ({ ...prev, operationType: value }));
                    setPage((prev) => ({ ...prev, current: 1 }));
                  }}
                />
                <RangePicker
                  aria-label="时间范围"
                  value={filters.range}
                  onChange={(value) => {
                    setFilters((prev) => ({ ...prev, range: value && value[0] && value[1] ? [value[0], value[1]] : null }));
                    setPage((prev) => ({ ...prev, current: 1 }));
                  }}
                />
                <Button type="primary" className="ddp-btn-dark" onClick={() => applyKeyword(keywordInput)}>
                  查询
                </Button>
              </ToolbarGroup>
              <ToolbarGroup>
                <Button type="text" onClick={resetFilters} disabled={!filtered}>
                  重置
                </Button>
              </ToolbarGroup>
            </>
          ) : undefined
        }
        footer={<Pager total={total} current={page.current} pageSize={page.size} onChange={(current, size) => setPage({ current, size })} />}
      >
        <Table
          columns={columns}
          dataSource={visibleRecords}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <EmptyState
                icon="zap"
                title={filtered ? "没有找到算力记录" : "暂无算力记录"}
                desc={filtered ? "检查账号 ID 或手机号是否正确，或放宽类型与时间范围。" : "账号发生充值、赠送或消耗后，会逐笔记录在这里。"}
                action={filtered ? <Button onClick={resetFilters}>清空筛选</Button> : undefined}
              />
            ),
          }}
        />
      </Surface>

      <Modal
        open={opOpen}
        onCancel={() => setOpOpen(false)}
        rootClassName="ddp-modal"
        width={480}
        destroyOnClose
        title={
          <span className="ddp-drawer-title">
            <span>调整算力</span>
            {account && (
              <span className="ddp-drawer-title__sub">
                {account.account?.username || `账号 ${account.accountId}`} · 当前余额 {formatNumber(account.balance)}
              </span>
            )}
          </span>
        }
        footer={[
          <Button key="cancel" onClick={() => setOpOpen(false)}>
            取消
          </Button>,
          <Button key="ok" type="primary" loading={opSaving} onClick={() => opForm.submit()}>
            确认调整
          </Button>,
        ]}
      >
        <Form form={opForm} layout="vertical" onFinish={submitOperation}>
          <Form.Item name="operationType" label="操作类型" rules={[{ required: true, message: "请选择操作类型" }]}>
            <Segmented
              block
              options={[
                { value: "recharge", label: "充值" },
                { value: "activity_gift", label: "活动赠送" },
                { value: "consume", label: "扣除" },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="operationAmount"
            label="数量"
            rules={[
              { required: true, message: "请输入操作数量" },
              { type: "number", min: 1, message: "操作数量必须大于0" },
            ]}
          >
            <InputNumber placeholder="请输入算力数量" style={{ width: "100%" }} min={1} precision={0} addonAfter="算力" />
          </Form.Item>
          <Form.Item name="taskId" label="关联任务 ID">
            <InputNumber placeholder="可选" style={{ width: "100%" }} min={1} precision={0} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea placeholder="例如：客服充值、活动补偿（可选）" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default TokenRecordsPage;
